import { useEffect, useMemo, useReducer, useRef } from 'react';
import { IOnboardingDocuments } from '@efex-services/documents/documentsSvc';
import { useApiCall } from 'use-api-call';
import { useSelector } from 'react-redux';
import { selectBusiness } from '@pages/profile/store/selectors';
import { UseApiCallProps } from '@pages/lending/types';
import {
  IStakeholderRequest,
  IStakeholderResponse,
} from '@pages/onboarding/types/newStakeholderResponse';
import clientSvc from '@efex-services/client/clientSvc';
import useStakeholderFileService from '@efex-components/EfexDS/FileInput/useStakeholderFileService';
import {
  IDocumentTypeId,
  IDocumentUsaTypeId,
} from '@efex-services/documents/types';
import StakeHolderContext from './StakeHolderContext';
import proofOfAddressDocumentReducer from './reducers/proofOfAddressDocumentReducer';
import { ProofOfAddressDocumentReducerCases } from './reducers/proofOfAddressDocumentReducer.types';
import govermentIdReducer from './reducers/govermentIdReducer';
import {
  DeleteGovernmentIdDocument,
  GovermentIdReducerCases,
} from './reducers/govermentIdReducer/types';

interface StreamFastProviderProps {
  children: React.ReactNode;
}

const StakeHolderProvider = ({ children }: StreamFastProviderProps) => {
  const [proofOfAddressDocument, dispatchProofOfAddressDocument] = useReducer(
    proofOfAddressDocumentReducer,
    []
  );

  const [govermentIdDocuments, dispatchGovermentIdDocuments] = useReducer(
    govermentIdReducer,
    {
      documentOne: null,
      documentTwo: null,
    }
  );

  const company = useSelector(selectBusiness);

  const {
    invoke: getStakeHolderV2,
  }: UseApiCallProps<IStakeholderResponse, IStakeholderRequest> = useApiCall(
    () =>
      clientSvc.getStakeHolderV2({ enterpriseId: company?.enterpriseId ?? '' })
  );

  const { uploadedFiles, handleChangeFile, handleDeleteFile } =
    useStakeholderFileService({ getStakeHolder: getStakeHolderV2 });
  const documentsToDeleteInBE = useRef<number[]>([]);

  const addDocumentToDelete = (documentId: number) => {
    documentsToDeleteInBE.current.push(documentId);
  };

  const deleteDocumensToDelete = async () => {
    await Promise.all(
      documentsToDeleteInBE.current.map((documentId) =>
        handleDeleteFile(documentId)
      )
    );
  };

  const setGovermentIdDocuments = (data: IOnboardingDocuments[]) => {
    const documents = data.filter(
      (document) =>
        document.document.type.id !== IDocumentTypeId.PROOF_OF_ADDRESS
    );

    dispatchGovermentIdDocuments({
      type: GovermentIdReducerCases.SET_DOCUMENTS,
      payload: documents,
    });
  };

  const handleChangeTypeOfGovernmentId = () => {
    const documentIds = Object.values(govermentIdDocuments).map(
      (document) => document?.id
    );

    if (documentIds.length) {
      documentIds.forEach((documentId) => {
        if (!documentId) return;
        addDocumentToDelete(documentId);
      });
    }

    setGovermentIdDocuments([]);
  };

  const addFirstGovernmentIdDocument = (document: IOnboardingDocuments) => {
    dispatchGovermentIdDocuments({
      type: GovermentIdReducerCases.SET_FIRST_DOCUMENT,
      payload: [document],
    });
  };

  const addSecondGovernmentIdDocument = (document: IOnboardingDocuments) => {
    dispatchGovermentIdDocuments({
      type: GovermentIdReducerCases.SET_SECOND_DOCUMENT,
      payload: [document],
    });
  };

  const deleteGovernmentIdDocument = (
    documentPlace: DeleteGovernmentIdDocument
  ) => {
    dispatchGovermentIdDocuments({
      type: GovermentIdReducerCases.DELETE_DOCUMENTS,
      payload: documentPlace,
    });

    const documentId =
      documentPlace === 'first'
        ? govermentIdDocuments.documentOne?.id
        : govermentIdDocuments.documentTwo?.id;

    if (documentId) {
      addDocumentToDelete(documentId);
    }
  };

  const setProofOfAddressDocument = (data: IOnboardingDocuments[]) => {
    dispatchProofOfAddressDocument({
      type: ProofOfAddressDocumentReducerCases.SET_DOCUMENT,
      payload: data,
    });
  };

  const changeProofOfAddressDocument = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    handleChangeFile(e);
  };

  const deleteProofOfAddressDocument = () => {
    const documentId = proofOfAddressDocument[0]?.id;

    dispatchProofOfAddressDocument({
      type: ProofOfAddressDocumentReducerCases.DELETE_DOCUMENT,
    });

    if (documentId) {
      addDocumentToDelete(documentId);
    }
  };

  const getAllCurrentDocumentsIds = () => {
    const idsArray: number[] = [];

    if (proofOfAddressDocument.length) {
      idsArray.push(proofOfAddressDocument[0].id);
    }

    Object.values(govermentIdDocuments).forEach(
      (document) => document && idsArray.push(document.id)
    );

    return idsArray;
  };

  useEffect(() => {
    const lastDocument = uploadedFiles[uploadedFiles.length - 1];

    if (!lastDocument) return;

    if (lastDocument.document.type.id === IDocumentTypeId.PROOF_OF_ADDRESS) {
      setProofOfAddressDocument([lastDocument]);
      return;
    }

    if (
      [
        IDocumentUsaTypeId.PASSPORT,
        IDocumentUsaTypeId.DRIVERS_LICENCE,
        IDocumentUsaTypeId.PERSONAL_ID,
      ].includes(lastDocument.document.type.id)
    ) {
      addFirstGovernmentIdDocument(lastDocument);
      return;
    }

    if (
      [
        IDocumentUsaTypeId.DRIVERS_LICENCE_BACK,
        IDocumentUsaTypeId.PERSONAL_ID_BACK,
      ].includes(lastDocument.document.type.id)
    )
      addSecondGovernmentIdDocument(lastDocument);
  }, [uploadedFiles]);

  const providerValue = useMemo(
    () => ({
      proofOfAddressDocument,
      govermentIdDocuments,
      setProofOfAddressDocument,
      setGovermentIdDocuments,
      deleteProofOfAddressDocument,
      addDocumentToDelete,
      deleteDocumensToDelete,
      changeProofOfAddressDocument,
      getAllCurrentDocumentsIds,
      handleChangeTypeOfGovernmentId,
      deleteGovernmentIdDocument,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [proofOfAddressDocument, govermentIdDocuments]
  );

  return (
    <StakeHolderContext.Provider value={providerValue}>
      {children}
    </StakeHolderContext.Provider>
  );
};

export default StakeHolderProvider;
